import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api, { authAPI } from '../services/api';
import { BASE_URL, REACT_APP_GOOGLE_CLIENT_ID, LEGAL_AVAILABLE, BILLING_VALIDATE_PROMO_REGISTRATION } from '../config/Config';
import { getPlanByCode } from '../config/Plans';
import {
  Box,
  TextField,
  Button,
  Typography,
  Divider,
  Alert,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';
import NavBar from '../components/common/NavBar';
import LegalPdfDialog from '../components/legal/LegalPdfDialog';
import StudentSelfConsentSection from '../components/legal/StudentSelfConsentSection';

const PLAN_LABELS = {
  student: {
    starter: 'Starter (Free)',
    pro: 'Pro (A$9.99/mo)',
    'teacher-assisted': 'Teacher Assisted (A$19.99/mo)'
  },
  teacher: {
    starter: 'Starter (Free)',
    pro: 'Pro (A$15/mo)',
   
  },
  parent: {
    starter: 'Starter (Free)',
    pro: 'Pro (A$9.99/mo)'
  }
};

// Shared styles so all input fields look consistent (no "greyed out" variants)
const INPUT_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.96)',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.6)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(102, 126, 234, 0.4)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.main',
    },
  },
  // Override browser autofill highlighting (which can add a blue/grey background)
  '& input:-webkit-autofill,& input:-webkit-autofill:hover,& input:-webkit-autofill:focus': {
    WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.96) inset',
    WebkitTextFillColor: 'inherit',
    caretColor: 'inherit',
    borderRadius: 999,
  },
};

// Helper functions to get plan details for payment page
const getPlanTitle = (planCode, role) => {
  const plan = getPlanByCode(planCode);
  return plan ? plan.title : 'Unknown Plan';
};

const getPlanPrice = (planCode, role) => {
  const plan = getPlanByCode(planCode);
  return plan ? plan.price : 'Unknown Price';
};

const getPlanPeriod = (planCode, role) => {
  const plan = getPlanByCode(planCode);
  return plan ? plan.period : '';
};

export const GoogleRegisterButton = ({
  selectedRole,
  planCode,
  onError,
  hasAcceptedTerms,
  studentSelfConsent = false,
  requiresLegalAcceptance = true,
  onValidateBeforeGoogleSignUp,
  disabled: disabledByLegal = false,
}) => {
  const navigate = useNavigate();

  const handleCredentialResponse = async (response) => {
    const id_token = response.credential;
    if (!selectedRole) {
      alert('Please select a role before continuing with Google sign-up.');
      return;
    }
    try {
      console.log('Google registration - selectedRole:', selectedRole);
      console.log('Google registration - planCode:', planCode);
      
      const requestBody = { 
        id_token: id_token, 
        role: selectedRole.toUpperCase(), 
        plan_code: planCode,
        agreeToTerms: requiresLegalAcceptance ? true : false,
        student_self_consent: !!studentSelfConsent,
      };
      
      console.log('Google registration - requestBody:', requestBody);
      

      
      const res = await fetch(`${BASE_URL}/api/accounts/register/google/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json();
      
      if (res.ok) {
        // ✅ NEW LOGIC: Handle all responses based on backend action field
        if (data && data.action) {
          const { action, message, redirect } = data;
          
          // Show the message from backend
          onError(message); // Use onError prop for success messages
          
          // Handle the action based on backend instruction
          switch (action) {
            case 'redirect_to_dashboard':
              navigate('/dashboard', {
                replace: true,
                state: {
                  message: message,
                  email: data.user?.email || data.email || 'user@example.com'
                }
              });
              break;
              
            case 'redirect_to_login':
              navigate('/login', {
                replace: true,
                state: {
                  message: message,
                  email: data.user?.email || data.email || 'user@example.com'
                }
              });
              break;
              
            case 'redirect_to_accept_invite_for_student_invites':
              // This case is no longer used - invitations are now marked as used during registration
              navigate('/login', {
                replace: true,
                state: {
                  message: message,
                  email: data.user?.email || data.email || 'user@example.com'
                }
              });
              break;
              
            case 'registration_success': {
              const msg = data.message || 'Registration successful! Check your email to verify and for your User Agreement copy.';
              onError(msg);
              navigate('/', { replace: true, state: { registrationSuccess: true, message: msg, email: data.user?.email || data.email } });
              return;
            }
            case 'redirect_to_checkout':
              if (data.checkout_url) {
                window.location.href = data.checkout_url;
              } else {
                navigate('/payment', { replace: true, state: { planCode: planCode, planTitle: getPlanTitle(planCode, selectedRole), planPrice: getPlanPrice(planCode, selectedRole), planPeriod: getPlanPeriod(planCode, selectedRole), changeReason: 'New registration', email: data.user?.email || data.email || 'user@example.com' } });
              }
              break;
              
            default:
              // Default behavior - redirect to login
              navigate('/login', {
                replace: true,
                state: {
                  message: message,
                  email: data.user?.email || data.email || 'user@example.com'
                }
              });
          }
          return;
        }
        
        // Fallback for responses without action field
        if (data && data.message) {
          const message = data.message;
          onError(message);
          
          // Default redirect to login
          navigate('/login', {
            replace: true,
            state: {
              message: message,
              email: data.user?.email || data.email || 'user@example.com'
            }
          });
        } else {
          // Fallback success message
          onError('Registration successful! Please check your email for verification.');
          navigate('/login', { replace: true });
        }
      } else {
        alert(data.error || 'Registration failed.');
        // clear any sticky auto-select so user can pick another account
        if (window.google?.accounts?.id?.disableAutoSelect) {
          window.google.accounts.id.disableAutoSelect();
        }
      }
    } catch (err) {
      alert('Not Registered yet. Redirecting to registration');
      // Redirect users to pricing page after alert
      navigate('/get-started');
    }
  };

  useEffect(() => {
    if (requiresLegalAcceptance && !hasAcceptedTerms) return;
    const interval = setInterval(() => {
      const google = window.google;
      const buttonEl = document.getElementById('google-register-btn');
      if (google && buttonEl) {
        google.accounts.id.initialize({
          client_id: REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false, // force account chooser every time
        });
        google.accounts.id.renderButton(buttonEl, {
          theme: 'outline',
          size: 'large',
          width: 280,
          text: 'signup_with',
          context: 'signup', // keep generic copy
        });
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, [selectedRole, hasAcceptedTerms, requiresLegalAcceptance]);

  if (disabledByLegal) {
    return (
      <Tooltip title="Registration is temporarily unavailable. The agreement document is not available.">
        <span>
          <Button
            variant="outlined"
            fullWidth
            disabled
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.2,
              px: 3,
            }}
          >
            Sign up with Google
          </Button>
        </span>
      </Tooltip>
    );
  }

  if (requiresLegalAcceptance && !hasAcceptedTerms) {
    return (
      <Button
        variant="outlined"
        fullWidth
        onClick={onValidateBeforeGoogleSignUp}
        sx={{
          borderRadius: '999px',
          textTransform: 'none',
          fontWeight: 600,
          py: 1.2,
          px: 3,
        }}
      >
        Sign up with Google
      </Button>
    );
  }

  return <div id="google-register-btn"></div>;
};

const Register = ({ onError }) => {
  const [selectedRole, setSelectedRole] = useState("STUDENT");
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    role: 'STUDENT',
    agreeToTerms: false,
    promoCode: '',
    studentSelfConsent: false,
  });
  const [searchParams] = useSearchParams();
  const roleFromURL = !!searchParams.get('role');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [legalDialogOpen, setLegalDialogOpen] = useState(false);
  const [legalDocumentAvailable, setLegalDocumentAvailable] = useState(null);
  const [studentLegalDocumentAvailable, setStudentLegalDocumentAvailable] = useState(null);
  const [promoValidation, setPromoValidation] = useState({ status: 'idle', message: '', original_price: null, discounted_price: null });
  const navigate = useNavigate();

  const reportTermsValidity = () => {
    const termsCheckbox = document.querySelector('input[name="agreeToTerms"]');
    if (!termsCheckbox) return true;
    return termsCheckbox.reportValidity();
  };

  const validateRegistrationBeforeSubmit = () => {
    if (requiresLegalAcceptance && !formData.agreeToTerms) {
      return reportTermsValidity();
    }
    return true;
  };

  useEffect(() => {
    // Regular registration flow - must come from pricing page
    const roleParam = location.state?.role;
    const planCode = location.state?.planCode;
    const planParam = location.state?.plan;
    const planTitle = location.state?.planTitle;
    const planPrice = location.state?.planPrice;
    const planPeriod = location.state?.planPeriod;
    const referralCode = location.state?.referralCode;
    const validRoles = ['STUDENT', 'TEACHER', 'PARENT'];

    // Convert roleParam to uppercase for validation since validRoles uses uppercase
    const roleParamUpper = roleParam?.toUpperCase();

    // Only validate that the role is one of the supported roles and that a plan slug is present.
    // The actual plan codes/slugs now come from config (e.g. Family Starter/Pro/Teacher assisted),
    // so we deliberately avoid hard-coding specific plan names here.
    if (!roleParam || !planParam || !validRoles.includes(roleParamUpper)) {
      navigate('/get-started', { replace: true });
      return;
    }

    setSelectedRole(roleParamUpper);
    setFormData((prev) => ({ ...prev, role: roleParamUpper }));
    sessionStorage.setItem('selectedPlan', planCode);
    // Store plan display information
    if (planTitle && planPrice) {
      sessionStorage.setItem('planTitle', planTitle);
      sessionStorage.setItem('planPrice', planPrice);
      sessionStorage.setItem('planPeriod', planPeriod || '');
    }
  }, [location.state, searchParams, navigate]);

  // Function to get cookie value
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  // Check for referral code in cookies if not passed via location state
  useEffect(() => {
    if (!location.state?.referralCode) {
      const refCode = getCookie('ref_code');
      if (refCode) {
        // Store referral code in session storage for later use
        sessionStorage.setItem('referralCode', refCode);
      }
    } else {
      // Store referral code from location state
      sessionStorage.setItem('referralCode', location.state.referralCode);
    }
  }, [location.state?.referralCode]);

  // Check for invite email from session storage and prefill email field
  useEffect(() => {
    const inviteEmail = sessionStorage.getItem('inviteEmail');
    if (inviteEmail) {
      setFormData(prev => ({ ...prev, email: inviteEmail }));
      // Clear the invite email from session storage after using it
      sessionStorage.removeItem('inviteEmail');
    }
  }, []);

  // Electronic signing only when original agreement document is available
  useEffect(() => {
    api.get(LEGAL_AVAILABLE)
      .then((res) => setLegalDocumentAvailable(res.data?.available === true))
      .catch(() => setLegalDocumentAvailable(false));
  }, []);

  const selectedPlanSlug = sessionStorage.getItem('selectedPlan');
  const selectedRoleSlug = searchParams.get('role');
  const planTitle = sessionStorage.getItem('planTitle') || location.state?.planTitle;
  const planPrice = sessionStorage.getItem('planPrice') || location.state?.planPrice;
  const planPeriod = sessionStorage.getItem('planPeriod') || location.state?.planPeriod || '';
  
  // Disable role toggle if role is passed from pricing page
  const isRoleToggleDisabled = !!searchParams.get('role') || location.state?.role;
  const selectedPlan = location.state?.planCode || selectedPlanSlug || 'student_starter';
  const showsStudentDeclaration = selectedRole === 'STUDENT';
  const requiresLegalAcceptance = selectedRole !== 'STUDENT' || formData.studentSelfConsent;
  
  // Create plan label with actual title and price
  const planLabel = planTitle && planPrice 
    ? `${planTitle} (${planPrice}${planPeriod})`
    : PLAN_LABELS?.[selectedRoleSlug]?.[selectedPlanSlug] || selectedPlanSlug;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (name === 'studentSelfConsent' && !checked) {
      setFormData(prev => ({
        ...prev,
        agreeToTerms: false,
      }));
    }
    if (name === 'promoCode') {
      setPromoValidation(prev => ({ ...prev, status: 'idle', message: '' }));
    }
  };

  const handlePromoBlur = async () => {
    const code = (formData.promoCode || '').trim();
    if (!code || !selectedPlan) {
      setPromoValidation(prev => ({ ...prev, status: 'idle', message: '' }));
      return;
    }
    setPromoValidation(prev => ({ ...prev, status: 'validating', message: '' }));
    try {
      const { data } = await api.post(BILLING_VALIDATE_PROMO_REGISTRATION, {
        code,
        plan_code: selectedPlan,
      });
      if (data.valid) {
        setPromoValidation({
          status: 'valid',
          message: data.message || 'Code applied',
          original_price: data.original_price ?? null,
          discounted_price: data.discounted_price ?? null,
        });
      } else {
        setPromoValidation({
          status: 'invalid',
          message: data.message || 'Invalid Code',
          original_price: null,
          discounted_price: null,
        });
      }
    } catch (err) {
      setPromoValidation({
        status: 'invalid',
        message: err.response?.data?.message || 'Invalid Code',
        original_price: null,
        discounted_price: null,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateRegistrationBeforeSubmit()) {
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Debug: Log all values before constructing registrationData
   
      
      const registrationData = {
        email: formData.email,
        role: selectedRole.toUpperCase(),
        plan_code: selectedPlan,
        agreeToTerms: !!formData.agreeToTerms,
        student_self_consent: !!formData.studentSelfConsent,
      };
      const promoTrimmed = (formData.promoCode || '').trim();
      if (promoTrimmed) registrationData.promo_code = promoTrimmed;

      // Debug logging
      console.log('Registration data:', registrationData);
      console.log('selectedPlan:', selectedPlan);
      console.log('selectedRole:', selectedRole);

      // Add referral code if available
      const referralCode = sessionStorage.getItem('referralCode');
      if (referralCode) {
        registrationData.referral_code = referralCode;
      }

     



      const response = await authAPI.register(registrationData);
      
      console.log('Registration response:', response);
      
      // Extract the actual response data from the Axios response
      const responseData = response.data || response;
      
      // ✅ NEW LOGIC: Handle all responses based on backend action field
      if (responseData && responseData.action) {
        const { action, message, redirect } = responseData;
        
        // Show the message from backend
        setSuccess(message);
        onError(message);
        
        // Handle the action based on backend instruction
        switch (action) {
          case 'redirect_to_dashboard':
            navigate('/dashboard', {
              replace: true,
              state: {
                message: message,
                email: responseData.email || formData.email
              }
            });
            break;
            
          case 'redirect_to_login':
            navigate('/login', {
              replace: true,
              state: {
                message: message,
                email: responseData.email || formData.email,
                needsEmailVerification: responseData.needsEmailVerification || false
              }
            });
            break;
            
                      case 'redirect_to_login_after_verification':
                navigate('/login', {
                    replace: true,
                    state: {
                        message: message,
                        email: responseData.email || formData.email,
                        needsEmailVerification: true
                    }
                });
                break;
                
            case 'redirect_to_accept_invite_for_student_invites':
                navigate('/accept-invite', {
                    replace: true,
                    state: {
                        message: message,
                        email: responseData.email || formData.email,
                        hasPendingStudentInvites: true,
                        pendingStudentInviteCount: responseData.pendingStudentInviteCount
                    }
                });
                break;
                
            case 'registration_success': {
                const msg = responseData.message || 'Registration successful! Check your email to verify and for your User Agreement copy.';
                setSuccess(msg);
                onError(msg);
                navigate('/', { replace: true, state: { registrationSuccess: true, message: msg, email: responseData.email || formData.email } });
                return;
            }
            case 'redirect_to_checkout':
                if (responseData.checkout_url) {
                  window.location.href = responseData.checkout_url;
                } else {
                  navigate('/payment', { replace: true, state: { planCode: selectedPlan, planTitle: getPlanTitle(selectedPlan, selectedRole), planPrice: getPlanPrice(selectedPlan, selectedRole), planPeriod: getPlanPeriod(selectedPlan, selectedRole), changeReason: 'New registration', email: responseData.email || formData.email } });
                }
                break;
            
          default:
            // Default behavior - redirect to login
            navigate('/login', {
              replace: true,
              state: {
                message: message,
                email: responseData.email || formData.email
              }
            });
        }
        return;
      }
      
      // Fallback for responses without action field
      if (responseData && responseData.message) {
        setSuccess(responseData.message);
        onError(responseData.message);
        
        // Check if this is a verification-related message
        if (responseData.verification_sent) {
          navigate('/login', {
            replace: true,
            state: {
              message: responseData.message,
              email: responseData.email || formData.email,
              needsEmailVerification: true
            }
          });
        } else {
          // Default redirect to home page
          navigate('/', { replace: true });
        }
      } else {
        // Fallback success message then redirect to home page
        setSuccess('Registration successful! Please check your email for verification.');
        onError('Registration successful! Please check your email for verification.');
        navigate('/', { replace: true });
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle different error response formats
      let errorMessage = 'Registration failed';
      
      if (error.response?.data) {
        const responseData = error.response.data;
        
        // Check if error is in specific fields first (serializer validation errors)
        if (responseData.email && Array.isArray(responseData.email) && responseData.email.length > 0) {
          errorMessage = responseData.email[0];
        } else if (responseData.password && Array.isArray(responseData.password) && responseData.password.length > 0) {
          // Surface password validation errors like "This password is too common."
          errorMessage = responseData.password[0];
        }
        // Check if error is in general error field
        else if (responseData.error) {
          errorMessage = responseData.error;
        }
        // Check if error is a plain string
        else if (typeof responseData === 'string') {
          errorMessage = responseData;
        }
        // Fallback: try to extract first string from any field (e.g. { field: ['msg'] })
        else if (typeof responseData === 'object' && responseData !== null) {
          const firstKey = Object.keys(responseData)[0];
          const val = firstKey ? responseData[firstKey] : null;
          if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
            errorMessage = val[0];
          } else if (typeof val === 'string') {
            errorMessage = val;
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #667eea 50%, #764ba2 75%, #f093fb 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(30, 60, 114, 0.1) 0%, rgba(42, 82, 152, 0.1) 25%, rgba(102, 126, 234, 0.1) 50%, rgba(118, 75, 162, 0.1) 75%, rgba(240, 147, 251, 0.1) 100%)',
        zIndex: 0
      }
    }}>
      <Box sx={{ position: 'relative', zIndex: 10 }}>
        <NavBar />
      </Box>
      
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        p: 2,
        position: 'relative',
        zIndex: 1
      }}>
        <Box sx={{ 
          width: '100%', 
          maxWidth: 500, 
          background: 'rgba(255, 255, 255, 0.7)', 
          backdropFilter: 'blur(20px)', 
          borderRadius: 4, 
          boxShadow: '0 8px 32px rgba(255, 255, 255, 0.15)', 
          p: { xs: 4, sm: 5 }, 
          border: '1px solid rgba(255, 255, 255, 0.3)', 
          position: 'relative', 
          overflow: 'hidden', 
          '&:before': { 
            content: '""', 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            height: 6, 
            background: 'linear-gradient(90deg, #1e3c72 0%, #667eea 50%, #f093fb 100%)' 
          } 
        }}>
          <Box sx={{ textAlign: 'center', mb: 5, mt: 2 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: '900',
                background: 'linear-gradient(45deg, #1e3c72, #2a5298)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem' }
              }}
            >
              Create Your Account
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                fontSize: '1.1rem',
                lineHeight: 1.6,
                mb: 3
              }}
            >
              Join us to get started
            </Typography>
            
            {/* Role display - moved here immediately after "Join us to get started" */}
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                I am a...
                {isRoleToggleDisabled && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
                    (Role pre-selected from pricing page)
                  </Typography>
                )}
              </Typography>
              <Box sx={{ 
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                color: '#0d47a1', 
                px: 4, 
                py: 1.5, 
                borderRadius: '20px', 
                display: 'inline-block', 
                fontWeight: 700,
                border: '2px solid #bbdefb',
                boxShadow: '0 4px 16px rgba(187, 222, 251, 0.3)'
              }}>
                {formData.role.charAt(0).toUpperCase() + formData.role.slice(1)}
              </Box>
            </Box>

            {planLabel && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #FF6A3D 0%, #FF9E4F 50%, #FFD15C 100%)',
                    color: '#fff', 
                    px: 3, 
                    py: 1, 
                    borderRadius: '20px', 
                    display: 'inline-block', 
                    fontWeight: 600,
                    border: '1px solid rgba(255, 106, 61, 0.3)',
                    fontSize: '0.9rem',
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                >
                  Plan: {planTitle}{' '}
                  {promoValidation.status === 'valid' && promoValidation.original_price != null && promoValidation.discounted_price != null ? (
                    <span>
                      (<span style={{ textDecoration: 'line-through' }}>{planPrice}{planPeriod}</span>
                      {' → A$'}{Number(promoValidation.discounted_price).toFixed(2)}{planPeriod || '/month'})
                    </span>
                  ) : (
                    `(${planPrice}${planPeriod})`
                  )}
                </Typography>
                <Button
                  component={Link}
                  to="/get-started"
                  size="small"
                  variant="text"
                  sx={{
                    fontSize: '12px',
                    mt: 1,
                    background: 'linear-gradient(to right, #FF6A3D, #FF9E4F, #FFD15C)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 600,
                    textDecoration: 'underline',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(to right, #FF5A2D, #FF8E3F, #FFC14C)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      textDecoration: 'none',
                      transform: 'translateY(-1px)'
                    },
                  }}
                >
                  Change
                </Button>
                <Box sx={{ mt: 2.5, width: '100%', maxWidth: 280 }}>
                  <TextField
                    fullWidth
                    label="Promo code (optional)"
                    name="promoCode"
                    value={formData.promoCode || ''}
                    onChange={handleChange}
                    onBlur={handlePromoBlur}
                    placeholder="Enter promo code"
                    sx={INPUT_FIELD_SX}
                  />
                </Box>
                {promoValidation.status === 'validating' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Verifying code...</Typography>
                  </Box>
                )}
                {promoValidation.status === 'valid' && promoValidation.message && (
                  <Alert
                    severity="success"
                    icon={false}
                    sx={{
                      width: '100%',
                      maxWidth: 280,
                      py: 0.5,
                      mt: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      '& .MuiAlert-message': { textAlign: 'center', flex: 1 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <CheckCircle fontSize="small" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Applied
                      </Typography>
                    </Box>
                  </Alert>
                )}
                {promoValidation.status === 'invalid' && promoValidation.message && (
                  <Alert
                    severity="error"
                    icon={false}
                    sx={{
                      width: '100%',
                      maxWidth: 280,
                      py: 0.5,
                      mt: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      '& .MuiAlert-message': { textAlign: 'center', flex: 1 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}>
                      <ErrorOutline fontSize="small" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {promoValidation.message}
                      </Typography>
                    </Box>
                  </Alert>
                )}
              </Box>
            )}
          </Box>

          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 3, borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField 
              fullWidth 
              label="Email" 
              name="email" 
              type="email" 
              value={formData.email} 
              onChange={handleChange} 
              required 
              autoComplete="off"
              inputProps={{ 'data-testid': 'register-email' }}
              sx={{ mt: 3, ...INPUT_FIELD_SX }} 
            />

            {showsStudentDeclaration && (
              <StudentSelfConsentSection
                studentSelfConsent={formData.studentSelfConsent}
                agreeToTerms={formData.agreeToTerms}
                onStudentSelfConsentChange={(checked) => handleChange({ target: { name: 'studentSelfConsent', type: 'checkbox', checked } })}
                onAgreeToTermsChange={(checked) => handleChange({ target: { name: 'agreeToTerms', type: 'checkbox', checked } })}
                onLegalAvailabilityChange={setStudentLegalDocumentAvailable}
                consentTestId="register-student-self-consent"
                termsTestId="register-terms-checkbox"
              />
            )}

            {selectedRole !== 'STUDENT' && requiresLegalAcceptance && legalDocumentAvailable === false && (
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                Registration is temporarily unavailable. The agreement document is not available. Please try again later or contact support.
              </Alert>
            )}
            {selectedRole !== 'STUDENT' && requiresLegalAcceptance && (
              <FormControlLabel 
                control={
                  <Checkbox 
                    name="agreeToTerms" 
                    checked={formData.agreeToTerms} 
                    onChange={handleChange} 
                    required 
                    disabled={legalDocumentAvailable !== true}
                    inputProps={{ 'data-testid': 'register-terms-checkbox' }}
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main'
                      }
                    }}
                  />
                } 
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
                    I agree to the{' '}
                    <Typography
                      component="span"
                      onClick={(e) => {
                        e.preventDefault();
                        if (legalDocumentAvailable === true) setLegalDialogOpen(true);
                      }}
                      sx={{
                        color: '#1e3c72',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        cursor: legalDocumentAvailable === true ? 'pointer' : 'not-allowed',
                        opacity: legalDocumentAvailable === true ? 1 : 0.6
                      }}
                    >
                      Terms of Service
                    </Typography>
                    {' '}and{' '}
                    <Typography
                      component="span"
                      onClick={(e) => {
                        e.preventDefault();
                        if (legalDocumentAvailable === true) setLegalDialogOpen(true);
                      }}
                      sx={{
                        color: '#1e3c72',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        cursor: legalDocumentAvailable === true ? 'pointer' : 'not-allowed',
                        opacity: legalDocumentAvailable === true ? 1 : 0.6
                      }}
                    >
                      Privacy Policy
                    </Typography>
                  </Typography>
                } 
                sx={{ mt: 3, mb: 3 }} 
              />
            )}

            <LegalPdfDialog
              open={legalDialogOpen}
              onClose={() => setLegalDialogOpen(false)}
              onAccept={() => setFormData(prev => ({ ...prev, agreeToTerms: true }))}
              pdfUrl="/api/accounts/legal/document/"
              title="Terms of Service & Privacy Policy"
            />

            <Button 
              type="submit" 
              fullWidth 
              variant="contained" 
              color="primary" 
              size="large" 
              disabled={
                isLoading ||
                (selectedRole !== 'STUDENT' && requiresLegalAcceptance && legalDocumentAvailable !== true) ||
                (selectedRole === 'STUDENT' && formData.studentSelfConsent && studentLegalDocumentAvailable !== true)
              } 
              data-testid="register-submit"
              sx={{ 
                mt: 2, 
                mb: 3,
                py: 1.5,
                borderRadius: '50px',
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                background: 'linear-gradient(to right, #FF6A3D, #FF9E4F, #FFD15C)',
                boxShadow: '0 8px 25px rgba(255, 106, 61, 0.4)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 35px rgba(255, 106, 61, 0.5)',
                  background: 'linear-gradient(to right, #FF5A2D, #FF8E3F, #FFC14C)'
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  transform: 'none',
                  boxShadow: 'none'
                }
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  Creating Account...
                </>
              ) : 'Create Account'}
            </Button>
          </form>

          <Divider sx={{ 
            my: 4, 
            '&:before, &:after': { 
              borderColor: 'rgba(102, 126, 234, 0.2)' 
            } 
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              or
            </Typography>
          </Divider>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <GoogleRegisterButton
              selectedRole={formData.role}
              planCode={selectedPlan}
              onError={onError}
              hasAcceptedTerms={formData.agreeToTerms}
              studentSelfConsent={formData.studentSelfConsent}
              requiresLegalAcceptance={requiresLegalAcceptance}
              onValidateBeforeGoogleSignUp={validateRegistrationBeforeSubmit}
              disabled={requiresLegalAcceptance && legalDocumentAvailable !== true}
            />
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
              Already have an account?{' '}
              <Link 
                to="/login" 
                style={{ 
                  fontWeight: 600, 
                  color: '#1e3c72',
                  textDecoration: 'none'
                }}
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Register;
